import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { bffApi, UploadStatusValueDto } from "../../autogen/bff-api";
import { useDocumentsUploader } from "../documents/UppyUploader/useDocumentsUploader";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useAppDispatch } from "../redux/hooks";
import { fetchFresh } from "../redux/fetchFresh";
import {
  conversationFileRefusal,
  extensionOf,
  type ConversationAttachment,
  type ConversationFileRefusal,
} from "./conversationFiles";

const STATUS_POLL_MS = 1500;
const READY_TIMEOUT_MS = 120_000;

export type ConversationFileFailure = "upload" | "refused" | "timeout";

export type PendingConversationFile = {
  id: string;
  fileName: string;
  state: "uploading" | "ready" | "failed";
  file: File;
  documentId?: string;
  failure?: ConversationFileFailure;
};

type Scanned = { documentId: string } | { failure: ConversationFileFailure };

export type ConversationFileProblem = ConversationFileRefusal;

const noRefetch = () => undefined;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useConversationFiles = () => {
  const dispatch = useAppDispatch();
  const personId = useLoggedInWithOrgContextState().me.id;
  const [files, setFiles] = useState<PendingConversationFile[]>([]);
  const [problem, setProblem] = useState<ConversationFileProblem | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const { upload } = useDocumentsUploader({
    entityType: "AssistantConversation",
    entityId: personId,
    refetch: noRefetch,
    reportsFailures: false,
    listensOnEntityChannel: false,
  });

  const settle = useCallback((id: string, patch: Partial<PendingConversationFile>) => {
    if (mounted.current) {
      setFiles((current) => current.map((file) => (file.id === id ? { ...file, ...patch } : file)));
    }
  }, []);

  const waitUntilScanned = useCallback(
    async (uploadId: string): Promise<Scanned> => {
      const giveUpAt = Date.now() + READY_TIMEOUT_MS;
      while (mounted.current && Date.now() < giveUpAt) {
        const status = await fetchFresh(
          dispatch(bffApi.endpoints.getConversationFileStatus.initiate({ uploadId }, { forceRefetch: true })),
        ).catch(() => null);
        if (status?.status === UploadStatusValueDto.Confirmed && status.documentId) return { documentId: status.documentId };
        if (status?.status === UploadStatusValueDto.Failed) return { failure: "refused" };
        await delay(STATUS_POLL_MS);
      }
      return { failure: "timeout" };
    },
    [dispatch],
  );

  const uploadAs = useCallback(
    async (entryId: string, file: File) => {
      const uploadId = uuid();
      const uploaded = await upload({ id: uploadId, name: file.name, extension: extensionOf(file.name), file });
      const scanned: Scanned = uploaded ? await waitUntilScanned(uploadId) : { failure: "upload" };
      settle(
        entryId,
        "documentId" in scanned
          ? { state: "ready", documentId: scanned.documentId, failure: undefined }
          : { state: "failed", failure: scanned.failure },
      );
    },
    [upload, waitUntilScanned, settle],
  );

  const attach = useCallback(
    async (file: File) => {
      const refusal = conversationFileRefusal(file);
      if (refusal) {
        setProblem(refusal);
        return;
      }
      setProblem(null);
      const id = uuid();
      setFiles((current) => [...current, { id, fileName: file.name, state: "uploading", file }]);
      await uploadAs(id, file);
    },
    [uploadAs],
  );

  const retry = useCallback(
    async (id: string) => {
      const failed = files.find((entry) => entry.id === id && entry.state === "failed");
      if (!failed) return;
      settle(id, { state: "uploading" });
      await uploadAs(id, failed.file);
    },
    [files, settle, uploadAs],
  );

  const remove = useCallback((id: string) => {
    setFiles((current) => current.filter((file) => file.id !== id));
    setProblem(null);
  }, []);

  const takeReady = useCallback((): ConversationAttachment[] => {
    const ready = files.flatMap((file) =>
      file.state === "ready" && file.documentId ? [{ documentId: file.documentId, fileName: file.fileName }] : [],
    );
    setFiles([]);
    setProblem(null);
    return ready;
  }, [files]);

  return {
    files,
    problem,
    attach,
    retry,
    remove,
    takeReady,
    uploading: files.some((file) => file.state === "uploading"),
    hasReady: files.some((file) => file.state === "ready"),
  };
};
